import { supabase } from "./client";
import {
  buildBranchPaymentSetupSnapshot,
  normalizePaymentProvider,
  resolveBranchCommissionRateByChannel,
} from "../local/paymentSettings";

const KIOSK_PAYMENT_FUNCTION_NAME = "mp-create-kiosk-payment";
const KIOSK_PAYMENT_REFRESH_FUNCTION_NAME = "mp-refresh-kiosk-payment-status";
const KIOSK_PAYMENT_CANCEL_FUNCTION_NAME = "mp-cancel-kiosk-payment";
const DIGITAL_PAYMENT_REFUND_FUNCTION_NAME = "mp-refund-digital-payment";
const PUBLIC_PAYMENT_INTENT_RPC_NAME = "get_public_branch_digital_payment_intent";
const APPROVED_KIOSK_PAYMENT_STATUSES = new Set(["approved", "paid", "closed"]);

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeQrPayload = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const kind = toSafeText(value.kind, 80).toLowerCase();
  const imageUrl = toSafeText(value.imageUrl, 1200);
  const copyCode = toSafeText(value.copyCode || value.copy_code, 4000);
  const qrData = toSafeText(value.qrData || value.qr_data, 4000);
  const deeplinkUrl = toSafeText(value.deeplinkUrl || value.linkUrl, 1200);
  const externalPosId = toSafeText(value.externalPosId, 160);
  const mode = toSafeText(value.mode, 40).toLowerCase();

  if (!imageUrl && !copyCode && !deeplinkUrl && !qrData) {
    return null;
  }

  return {
    kind: kind || (deeplinkUrl && !imageUrl ? "checkout_link" : ""),
    imageUrl,
    copyCode,
    qrData,
    deeplinkUrl,
    externalPosId,
    mode,
  };
};

const DEFAULT_PUBLIC_KIOSK_PAYMENT_ORIGIN = "https://beta.mozzoapp.com";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );

  if (
    backendMessage &&
    transportMessage.toLowerCase().includes("non-2xx")
  ) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

const isMissingPublicPaymentIntentRpcError = (error) => {
  const message = toSafeText(
    `${error?.message || ""} ${error?.details || ""}`,
    400
  ).toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes(PUBLIC_PAYMENT_INTENT_RPC_NAME)
  );
};

const isMissingKioskPaymentRefreshFunctionError = (error) => {
  const message = toSafeText(
    `${error?.message || ""} ${error?.details || ""}`,
    400
  ).toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes(KIOSK_PAYMENT_REFRESH_FUNCTION_NAME)
  );
};

const isUuid = (value) => UUID_PATTERN.test(toSafeText(value, 80));

const buildClientIntentId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20, 32),
    ].join("-");
  }

  return "";
};

export const normalizeKioskDigitalPaymentIntent = (value = {}) => {
  const provider = normalizePaymentProvider(value.provider);

  return {
    id: toSafeText(value.id || value.intentId || "", 120),
    status: toSafeText(value.status || "created", 40).toLowerCase(),
    provider,
    externalReference: toSafeText(
      value.externalReference || value.providerExternalReference || "",
      160
    ),
    providerOperationId: toSafeText(
      value.providerOperationId || value.providerIntentId || "",
      160
    ),
    grossAmount: toMoney(value.grossAmount),
    retainedAmount: toMoney(value.retainedAmount),
    merchantNetAmount: toMoney(value.merchantNetAmount),
    currentCommissionAmount: toMoney(value.currentCommissionAmount),
    pendingDebtAppliedAmount: toMoney(value.pendingDebtAppliedAmount),
    expiresAt: toSafeText(value.expiresAt || "", 80),
    createdAt: toSafeText(value.createdAt || "", 80),
    approvedAt: toSafeText(value.approvedAt || value.approved_at || "", 80),
    locallyReleased: value.locallyReleased === true,
    qr: normalizeQrPayload(value.qr || value.qrPayload),
    raw: value,
  };
};

export const buildPublicKioskPaymentLandingUrl = ({
  intentId,
  orderId = "",
  branchId = "",
} = {}) => {
  const normalizedIntentId = toSafeText(intentId, 120);
  const normalizedOrderId = toSafeText(orderId, 120);
  const normalizedBranchId = toSafeText(branchId, 120);

  if (!normalizedIntentId || !normalizedBranchId) {
    return "";
  }

  try {
    const baseUrl =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : DEFAULT_PUBLIC_KIOSK_PAYMENT_ORIGIN;
    const url = new URL("/kiosk/pago", baseUrl);
    url.searchParams.set("intent", normalizedIntentId);
    url.searchParams.set("branch", normalizedBranchId);

    if (normalizedOrderId) {
      url.searchParams.set("order", normalizedOrderId);
    }

    return url.toString();
  } catch {
    return "";
  }
};

const isApprovedKioskDigitalPaymentIntent = (intent) => {
  const normalizedStatus = toSafeText(intent?.status || "", 40).toLowerCase();
  return (
    APPROVED_KIOSK_PAYMENT_STATUSES.has(normalizedStatus) ||
    Boolean(toSafeText(intent?.approvedAt || intent?.approved_at || "", 80))
  );
};

export const getPublicKioskDigitalPaymentIntent = async ({
  intentId,
  orderId = "",
  branchId = "",
  paymentId = "",
  merchantOrderId = "",
  preferenceId = "",
  externalReference = "",
  providerStatus = "",
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos revisar el estado del QR de cobro."
    );
  }

  const normalizedIntentId = toSafeText(intentId, 120);
  const normalizedOrderId = toSafeText(orderId, 120);
  const normalizedBranchId = toSafeText(branchId, 120);

  if (!normalizedIntentId) {
    return null;
  }

  let refreshFunctionUnavailable = false;

  try {
    const { data, error } = await supabase.functions.invoke(
      KIOSK_PAYMENT_REFRESH_FUNCTION_NAME,
      {
        body: {
          branchId: normalizedBranchId,
          intentId: normalizedIntentId,
          orderId: normalizedOrderId,
          paymentId: toSafeText(paymentId, 160),
          merchantOrderId: toSafeText(merchantOrderId, 160),
          preferenceId: toSafeText(preferenceId, 160),
          externalReference: toSafeText(externalReference, 160),
          providerStatus: toSafeText(providerStatus, 80),
        },
      }
    );

    if (error) {
      const errorPayload = await extractFunctionErrorPayload(error, data);

      if (!isMissingKioskPaymentRefreshFunctionError(errorPayload || error)) {
        throw new Error(
          normalizeFunctionError(
            error,
            errorPayload,
            "No pudimos revisar si el cobro QR del kiosk ya fue aprobado."
          )
        );
      }

      refreshFunctionUnavailable = true;
    } else if (data && typeof data === "object") {
      return normalizeKioskDigitalPaymentIntent(data);
    }
  } catch (error) {
    if (!isMissingKioskPaymentRefreshFunctionError(error)) {
      throw error;
    }

    refreshFunctionUnavailable = true;
  }

  const { data, error } = await supabase.rpc(PUBLIC_PAYMENT_INTENT_RPC_NAME, {
    p_intent_id: normalizedIntentId,
    p_order_id: normalizedOrderId || null,
    p_branch_id: normalizedBranchId || null,
  });

  if (error) {
    if (isMissingPublicPaymentIntentRpcError(error)) {
      throw new Error(
        "Falta desplegar mp-refresh-kiosk-payment-status o aplicar public_digital_payment_intent_status_patch.sql en Supabase para confirmar los pagos QR del kiosk."
      );
    }

    throw new Error(
      error.message ||
        "No pudimos revisar si el cobro QR del kiosk ya fue aprobado."
    );
  }

  if (!data || typeof data !== "object") {
    return null;
  }

  const normalizedIntent = normalizeKioskDigitalPaymentIntent(data);

  if (
    refreshFunctionUnavailable &&
    !isApprovedKioskDigitalPaymentIntent(normalizedIntent)
  ) {
    throw new Error(
      "El QR ya se genero, pero falta desplegar la verificacion automatica del pago del kiosk en Supabase. Hasta hacerlo, el pedido no va a imprimir ni cerrarse solo despues de pagar."
    );
  }

  return normalizedIntent;
};

// The refresh endpoint works for every in-person Mercado Pago QR intent.
// Keep the kiosk-named export for compatibility and expose a neutral name for new callers.
export const getDigitalPaymentIntent = getPublicKioskDigitalPaymentIntent;

export const cancelInPersonQrPaymentIntent = async ({
  branchId,
  intentId,
  orderId = "",
  sourceChannel = "waiter",
  releaseLocally = false,
  riskAcknowledged = false,
} = {}) => {
  if (releaseLocally && riskAcknowledged !== true) {
    throw new Error("Confirma que el QR anterior podria acreditarse antes de liberar el cobro.");
  }
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos cancelar el QR de cobro."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);
  const normalizedIntentId = toSafeText(intentId, 120);

  if (!normalizedBranchId || !isUuid(normalizedIntentId)) {
    throw new Error("Faltan datos del QR que quieres cancelar.");
  }

  const { data, error } = await supabase.functions.invoke(
    KIOSK_PAYMENT_CANCEL_FUNCTION_NAME,
    {
      body: {
        branchId: normalizedBranchId,
        intentId: normalizedIntentId,
        orderId: toSafeText(orderId, 160),
        sourceChannel: toSafeText(sourceChannel, 40) || "waiter",
        ...(releaseLocally ? { action: "release_local", riskAcknowledged: true } : {}),
      },
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);
    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos cancelar el QR de cobro."
      )
    );
  }

  if (
    toSafeText(data?.id, 120) !== normalizedIntentId ||
    !(releaseLocally
      ? data?.locallyReleased === true && data?.providerCancellationConfirmed === false &&
        Array.isArray(data?.state?.paymentOperations) && Array.isArray(data?.state?.mesasActivas)
      : ["cancelled", "canceled", "expired"].includes(toSafeText(data?.status, 80).toLowerCase()))
  ) {
    throw new Error(
      toSafeText(data?.error, 240) ||
        "No se confirmo la cancelacion del QR. Actualiza el estado antes de intentar otro cobro."
    );
  }

  return data;
};

export const refundApprovedDigitalPayment = async ({
  branchId,
  intentId = "",
  orderId = "",
  reason = "",
} = {}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos reintegrar este cobro."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);
  const normalizedIntentId = toSafeText(intentId, 120);
  const normalizedOrderId = toSafeText(orderId, 160);
  const normalizedReason = toSafeText(reason, 500);

  if (
    !normalizedBranchId ||
    (!isUuid(normalizedIntentId) && !normalizedOrderId)
  ) {
    throw new Error("Faltan datos del cobro que quieres reintegrar.");
  }

  if (normalizedReason.length < 5) {
    throw new Error("Indica brevemente el motivo del reintegro.");
  }

  const { data, error } = await supabase.functions.invoke(
    DIGITAL_PAYMENT_REFUND_FUNCTION_NAME,
    {
      body: {
        branchId: normalizedBranchId,
        intentId: normalizedIntentId,
        orderId: normalizedOrderId,
        reason: normalizedReason,
      },
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);
    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos reintegrar este cobro."
      )
    );
  }

  return data && typeof data === "object" ? data : {};
};

const normalizeInPersonQrSource = (value) =>
  toSafeText(value, 40).toLowerCase() === "waiter" ? "waiter" : "kiosk";

const getInPersonQrBlockingReason = ({ setup, sourceChannel }) => {
  if (sourceChannel === "kiosk") {
    return (
      setup.kioskQrBlockingReason ||
      "La sucursal todavia no esta lista para cobrar con QR desde el kiosk."
    );
  }

  if (setup.paymentProvider !== "mercado_pago") {
    return "El cobro QR presencial del mozo se habilita conectando Mercado Pago a MozzoApp.";
  }

  if (!setup.providerConnected) {
    return "La cuenta Mercado Pago de la sucursal todavia no esta conectada para cobrar desde el mozo.";
  }

  return "La sucursal todavia no esta lista para cobrar con QR presencial desde el mozo.";
};

export const createInPersonQrPaymentIntent = async ({
  branch,
  order,
  customerName = "",
  forceRefresh = false,
  sourceChannel = "kiosk",
}) => {
  const normalizedSourceChannel = normalizeInPersonQrSource(sourceChannel);
  const isWaiterCharge = normalizedSourceChannel === "waiter";

  if (!supabase) {
    throw new Error(
      isWaiterCharge
        ? "Supabase no esta configurado. No podemos crear el QR de cobro del mozo."
        : "Supabase no esta configurado. No podemos crear el QR de cobro del kiosk."
    );
  }

  const setup = buildBranchPaymentSetupSnapshot(branch);

  const inPersonQrConfigured =
    setup.paymentProvider === "mercado_pago" &&
    setup.providerConnected;
  const paymentIsConfigured = isWaiterCharge
    ? inPersonQrConfigured
    : setup.kioskDigitalConfigured;

  if (!paymentIsConfigured) {
    throw new Error(
      getInPersonQrBlockingReason({
        setup,
        sourceChannel: normalizedSourceChannel,
      })
    );
  }

  const branchId = toSafeText(branch?.id || "", 80);
  const orderId = toSafeText(order?.id || "", 120);
  const hintedIntentId = isUuid(order?.paymentIntentId)
    ? toSafeText(order?.paymentIntentId, 80)
    : buildClientIntentId();
  const paymentLandingUrl = isWaiterCharge
    ? ""
    : buildPublicKioskPaymentLandingUrl({
        intentId: hintedIntentId,
        orderId,
        branchId,
      });

  if (!branchId || !orderId) {
    throw new Error(
      isWaiterCharge
        ? "Faltan datos basicos de la cuenta para generar el QR de cobro del mozo."
        : "Faltan datos basicos del pedido para generar el QR de cobro del kiosk."
    );
  }

  const kioskStationNumber = isWaiterCharge
    ? 1
    : Number(order?.kioskStationNumber || 0) || null;
  const body = {
    intentId: hintedIntentId,
    branchId,
    orderId,
    paymentOperationId: toSafeText(order?.paymentOperationId || "", 120),
    provider: setup.paymentProvider,
    paymentAccountReference: setup.paymentAccountReference,
    paymentAccountAlias: setup.paymentAccountAlias,
    paymentAccountCbu: setup.paymentAccountCbu,
    commissionRate: resolveBranchCommissionRateByChannel(
      { ...branch, ...setup },
      "kiosk"
    ),
    paymentSplitMode: setup.paymentSplitMode,
    customerName: toSafeText(customerName || order?.customerName || "", 80),
    grossAmount: toMoney(order?.totalAmount ?? order?.pricing?.finalTotal),
    tableLabel: isWaiterCharge
      ? toSafeText(order?.mesa || order?.tableLabel || "Salon", 120)
      : Number(order?.kioskStationNumber || 0) > 0
        ? `Totem ${Number(order.kioskStationNumber)}`
        : "Totem",
    walletProvider: "mercado_pago",
    sourceChannel: normalizedSourceChannel,
    forceRefresh: forceRefresh === true,
    returnUrl:
      paymentLandingUrl ||
      (typeof window !== "undefined" ? toSafeText(window.location.href, 1400) : ""),
    preparationNumber: Number(order?.preparationNumber || 0) || null,
    // The backend reuses the branch's physical QR terminal for waiter charges.
    kioskStationNumber,
    serviceType: toSafeText(
      order?.serviceType || (isWaiterCharge ? "salon" : ""),
      40
    ),
  };

  const { data, error } = await supabase.functions.invoke(
    KIOSK_PAYMENT_FUNCTION_NAME,
    {
      body,
    }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);
    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        isWaiterCharge
          ? "No pudimos generar el QR de cobro presencial del mozo."
          : "No pudimos generar el QR de cobro del kiosk."
      )
    );
  }

  return normalizeKioskDigitalPaymentIntent(data);
};

// Compatibility export for the kiosk flow. New in-person channels should call
// the neutral creator above so both use the same dynamic Mercado Pago QR rail.
export const createKioskDigitalPaymentIntent = (options = {}) =>
  createInPersonQrPaymentIntent({
    ...options,
    sourceChannel: "kiosk",
  });
